import { Component, computed, effect, inject, input, model, OnDestroy, signal, Signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { AuthService } from 'src/app/auth.service';
import { LoadingState } from 'src/app/shared/components/ui/content-display-card/content-display-card.component';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { Chunk } from 'src/app/views/content/chunk/chunk.dto';
import { CreateChunkService } from 'src/app/views/content/chunk/create-chunk/create-chunk.service';
import { ContentStateService } from 'src/app/views/content/content-state.service';
import { ContentService } from 'src/app/views/content/content.service';
import { NotesService } from '../notes.service';
import { BreakpointObserverService } from 'src/app/shared/services/breakpoint-observer/breakpoint-observer.service';
import { CourseSettings } from 'src/app/views/displayer/courses/courses.dto';

@Component({
  selector: 'app-notes-viewer',
  templateUrl: './notes-viewer.component.html',
  styleUrls: ['./notes-viewer.component.scss'],
  standalone: false,
})
export class NotesViewerComponent implements OnDestroy {
  id = input.required<string>();
  note = model<any>(null);
  showContent = signal(true);

  chunk: Chunk;
  private chunkSubscription: Subscription;

  isReadyForIndexing: Signal<boolean> = this.contentStateService.isReadyForIndexing;
  loadingIndicatorState = computed<LoadingState>(() => this.contentStateService.loading());
  private readonly authService = inject(AuthService);
  private breakpointObserverService = inject(BreakpointObserverService);

  isCompact = computed(() => !this.breakpointObserverService.isLargeScreen());

  isReader = computed(() => {
    const userId = this.authService.getID();
    const note = this.note();
    if (note) {
      return note.creator !== userId;
    }
    return false;
  });

  constructor(
    private noteService: NotesService,
    private toast: ToastService,
    private translate: TranslateService,
    private createChunkService: CreateChunkService,
    private contentService: ContentService,
    public contentStateService: ContentStateService
  ) {
    effect(() => {
      const noteId = this.id();
      this.reloadContent(noteId);
    });

    this.chunkSubscription = this.createChunkService.chunk$.subscribe((chunkData) => {
      this.chunk = chunkData;
    });
  }
  // Hidden by default due to background indexing automation
  generateEmbeddingsForCurrentContent(): void {
    if (this.note() && this.note().content_id) {
      this.contentService.generateEmbeddingsForContentChunks(this.note().content_id);
    }
  }

  private reloadContent(noteId: string): void {
    this.showContent.set(false);

    setTimeout(() => {
      this.noteService
        .getNote(noteId)
        .then((fetchedNote) => {
          this.note.set(fetchedNote);
          this.showContent.set(true);
        })
        .catch((err) => {
          console.error(err);
          this.toast.snackbar.open(this.translate.instant('Snackbar.Error'), 'Ok', {
            duration: 2000,
          });
          this.showContent.set(true);
        });
    }, 0);
  }

  updateSettings(settings: CourseSettings) {
    const currentNote = this.note();
    if (!currentNote) return;
    this.note.set({ ...currentNote, settings });
    this.noteService.updateSettings(currentNote._id, settings).subscribe({
      next: () => {
        // Updated successfully
      },
      error: (error) => {
        console.error('Failed to update Settings for Note', error);
      }
    });
  }

  ngOnDestroy(): void {
    this.contentStateService.clear();
    this.chunkSubscription?.unsubscribe();
  }
}
